import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { workspacePlanService, workspaceService } from "@/services/workspaceService";
import type {
  WorkspaceAddress,
  WorkspaceCreatePayload,
  WorkspacePlanCreatePayload,
  WorkspacePlanUpdatePayload,
  WorkspaceSettingsUpdatePayload,
  WorkspaceUpdatePayload,
} from "@/types/workspace";

const KEYS = {
  mine: ["workspaces", "me"] as const,
  one: (id: number) => ["workspaces", id] as const,
  stats: (id: number) => ["workspaces", id, "stats"] as const,
  settings: (id: number) => ["workspaces", id, "settings"] as const,
  plans: ["workspaces", "plans"] as const,
};

export function useMyWorkspaces() {
  return useQuery({ queryKey: KEYS.mine, queryFn: workspaceService.listMine });
}

export function useWorkspace(id: number) {
  return useQuery({ queryKey: KEYS.one(id), queryFn: () => workspaceService.get(id) });
}

export function useWorkspaceStats(id: number) {
  return useQuery({ queryKey: KEYS.stats(id), queryFn: () => workspaceService.getStats(id) });
}

export function useWorkspaceSettings(id: number) {
  return useQuery({ queryKey: KEYS.settings(id), queryFn: () => workspaceService.getSettings(id) });
}

export function useCreateWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: WorkspaceCreatePayload) => workspaceService.create(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.mine }),
  });
}

export function useUpdateWorkspace(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: WorkspaceUpdatePayload) => workspaceService.update(id, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYS.mine });
      // exact: evita o prefix-matching de ["workspaces", id] refetchar stats/settings junto (PERF-008)
      void qc.invalidateQueries({ queryKey: KEYS.one(id), exact: true });
    },
  });
}

export function useSetWorkspaceAddress(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: WorkspaceAddress) => workspaceService.setAddress(id, payload),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEYS.one(id), exact: true }),
  });
}

export function useUpdateWorkspaceSettings(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: WorkspaceSettingsUpdatePayload) =>
      workspaceService.updateSettings(id, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYS.settings(id) });
      // useWorkspace/useMyWorkspaces embutem ws.settings — invalidar tambem (FE-017)
      void qc.invalidateQueries({ queryKey: KEYS.one(id), exact: true });
      void qc.invalidateQueries({ queryKey: KEYS.mine });
    },
  });
}

export function usePlans() {
  return useQuery({ queryKey: KEYS.plans, queryFn: workspacePlanService.list });
}

export function useCreatePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: WorkspacePlanCreatePayload) => workspacePlanService.create(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.plans }),
  });
}

export function useUpdatePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: WorkspacePlanUpdatePayload }) =>
      workspacePlanService.update(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.plans }),
  });
}
